import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BookOpen, Clock, Play, CheckCircle, XCircle, ArrowLeft,
  BarChart3, Target, TrendingUp, AlertTriangle, Loader2,
  ChevronRight, Award, RefreshCw, Volume2, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MockTests() {
  const [view, setView] = useState('subjects'); // subjects, exam, results, analytics
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [history, setHistory] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);
  const token = localStorage.getItem('auth_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSubjects = useCallback(async () => {
    try {
      const [subRes, histRes, anaRes] = await Promise.all([
        axios.get(`${API}/mock-tests/subjects`, { headers, withCredentials: true }),
        axios.get(`${API}/mock-tests/history`, { headers, withCredentials: true }),
        axios.get(`${API}/mock-tests/analytics`, { headers, withCredentials: true })
      ]);
      setSubjects(subRes.data);
      setHistory(histRes.data);
      setAnalytics(anaRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSubjects(); }, [fetchSubjects]);

  // Timer
  useEffect(() => {
    if (exam && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [exam]);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}:${m.toString().padStart(2,'0')}:${sec.toString().padStart(2,'0')}`;
  };

  const startExam = async (subjectId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/mock-tests/start`, { subject_id: subjectId }, { headers, withCredentials: true });
      setExam(res.data);
      setAnswers({});
      setTimeLeft(res.data.duration_minutes * 60);
      setView('exam');
      toast.success(`Exam started: ${res.data.subject}`);
    } catch (err) { toast.error('Failed to start exam'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const unanswered = exam.questions.filter(q => !answers[q.question_id]?.trim()).length;
    if (unanswered > 0 && timeLeft > 0) {
      if (!window.confirm(`You have ${unanswered} unanswered questions. Submit anyway?`)) return;
    }
    setSubmitting(true);
    clearInterval(timerRef.current);
    try {
      const res = await axios.post(`${API}/mock-tests/submit`, {
        exam_id: exam.exam_id, subject_id: exam.questions[0]?.subject_id || '', answers
      }, { headers, withCredentials: true });
      setResults(res.data);
      setView('results');
      toast.success(`Exam submitted! Score: ${res.data.score}/${res.data.total_marks}`);
    } catch (err) { toast.error('Submission failed'); }
    finally { setSubmitting(false); }
  };

  const viewPastResult = async (examId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mock-tests/results/${examId}`, { headers, withCredentials: true });
      setResults(res.data);
      setView('results');
    } catch (err) { toast.error('Failed to load results'); }
    finally { setLoading(false); }
  };

  if (loading && view === 'subjects') return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  // ==================== SUBJECTS VIEW ====================
  if (view === 'subjects') return (
    <div className="space-y-6" data-testid="mock-tests">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Mock Tests</h2>
          <p className="text-sm text-muted-foreground">B.Des Animation — RSTM University Gwalior Pattern</p>
        </div>
        {analytics && (
          <Badge className={`text-sm px-3 py-1 ${analytics.overall_readiness >= 60 ? 'bg-emerald-600' : analytics.overall_readiness >= 30 ? 'bg-amber-600' : 'bg-red-600'}`}>
            Exam Readiness: {analytics.overall_readiness}%
          </Badge>
        )}
      </div>

      {/* Subject Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {subjects.map(sub => {
          const subAnalytics = analytics?.subject_analytics?.find(a => a.subject_id === sub.subject_id);
          return (
            <Card key={sub.subject_id} className="hover:border-primary/50 transition-colors" data-testid={`subject-${sub.subject_id}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm">{sub.title}</h3>
                    <p className="text-xs text-muted-foreground">{sub.questions_available} questions</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{sub.description}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                  <Clock className="w-3 h-3" /> 3 hours
                  <span className="mx-1">|</span>
                  <Target className="w-3 h-3" /> 70 marks
                  <span className="mx-1">|</span>
                  25 MCQ + 5 Short + 2 Long
                </div>
                {subAnalytics && subAnalytics.attempts > 0 && (
                  <div className="p-2 rounded-lg bg-muted/50 mb-3 text-xs">
                    <div className="flex justify-between"><span>Attempts:</span><span className="font-medium">{subAnalytics.attempts}</span></div>
                    <div className="flex justify-between"><span>Best:</span><span className="font-medium text-emerald-400">{subAnalytics.best_percentage}%</span></div>
                    <div className="flex justify-between"><span>Latest:</span><span className="font-medium">{subAnalytics.latest_percentage}%</span></div>
                    {subAnalytics.improvement !== 0 && (
                      <div className="flex justify-between"><span>Improvement:</span>
                        <span className={subAnalytics.improvement > 0 ? 'text-emerald-400' : 'text-red-400'}>
                          {subAnalytics.improvement > 0 ? '+' : ''}{subAnalytics.improvement}%
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <Button onClick={() => startExam(sub.subject_id)} className="w-full" size="sm" data-testid={`start-${sub.subject_id}`}>
                  <Play className="w-4 h-4 mr-2" /> Start Mock Test
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Past Attempts */}
      {history.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Past Attempts</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.slice(0, 10).map(h => (
                <div key={h.exam_id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 cursor-pointer" onClick={() => viewPastResult(h.exam_id)}>
                  <div>
                    <p className="font-medium text-sm">{h.subject_title}</p>
                    <p className="text-xs text-muted-foreground">{new Date(h.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={h.percentage >= 60 ? 'bg-emerald-600' : h.percentage >= 40 ? 'bg-amber-600' : 'bg-red-600'}>
                      {h.score}/{h.total_marks} ({h.percentage}%)
                    </Badge>
                    <Eye className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // ==================== EXAM VIEW ====================
  if (view === 'exam' && exam) {
    const mcqs = exam.questions.filter(q => q.type === 'mcq');
    const shorts = exam.questions.filter(q => q.type === 'short');
    const longs = exam.questions.filter(q => q.type === 'long');
    const answered = Object.keys(answers).filter(k => answers[k]?.trim()).length;

    return (
      <div className="space-y-4" data-testid="exam-view">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur p-3 rounded-lg border flex items-center justify-between">
          <div>
            <h3 className="font-bold">{exam.subject}</h3>
            <p className="text-xs text-muted-foreground">{answered}/{exam.questions.length} answered</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={`font-mono text-sm ${timeLeft < 600 ? 'bg-red-600 animate-pulse' : 'bg-primary'}`}>
              <Clock className="w-3 h-3 mr-1" /> {formatTime(timeLeft)}
            </Badge>
            <Button size="sm" onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-exam-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit Exam</>}
            </Button>
          </div>
        </div>

        {/* Section A: MCQs */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Section A — Multiple Choice Questions (25 × 1 = 25 marks)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {mcqs.map((q, i) => (
              <div key={q.question_id} className="p-3 rounded-lg border" data-testid={`mcq-${i}`}>
                <p className="font-medium text-sm mb-2">Q{i+1}. {q.question} <Badge variant="outline" className="text-[10px] ml-1">{q.topic}</Badge></p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {q.options.map((opt, j) => (
                    <button key={j} onClick={() => setAnswers({...answers, [q.question_id]: opt})}
                      className={`text-left p-2 rounded-lg border text-sm transition-colors ${answers[q.question_id] === opt ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-accent/50'}`}>
                      <span className="font-medium mr-2">{String.fromCharCode(65+j)}.</span>{opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Section B: Short Answers */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Section B — Short Answer Questions (5 × 5 = 25 marks)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {shorts.map((q, i) => (
              <div key={q.question_id} className="p-3 rounded-lg border" data-testid={`short-${i}`}>
                <p className="font-medium text-sm mb-1">Q{mcqs.length+i+1}. {q.question}</p>
                <div className="flex gap-2 text-xs text-muted-foreground mb-2">
                  <Badge variant="outline" className="text-[10px]">{q.topic}</Badge>
                  <span>{q.marks} marks</span>
                  <span>Word limit: ~{q.word_limit}</span>
                </div>
                <Textarea value={answers[q.question_id] || ''} onChange={e => setAnswers({...answers, [q.question_id]: e.target.value})}
                  rows={4} placeholder="Write your answer here..." className="text-sm" />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Section C: Long Answers */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Section C — Long Answer Questions (2 × 10 = 20 marks)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {longs.map((q, i) => (
              <div key={q.question_id} className="p-3 rounded-lg border" data-testid={`long-${i}`}>
                <p className="font-medium text-sm mb-1">Q{mcqs.length+shorts.length+i+1}. {q.question}</p>
                <div className="flex gap-2 text-xs text-muted-foreground mb-2">
                  <Badge variant="outline" className="text-[10px]">{q.topic}</Badge>
                  <span>{q.marks} marks</span>
                  <span>Word limit: ~{q.word_limit}</span>
                </div>
                <Textarea value={answers[q.question_id] || ''} onChange={e => setAnswers({...answers, [q.question_id]: e.target.value})}
                  rows={8} placeholder="Write your detailed answer here..." className="text-sm" />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="text-center pb-8">
          <Button size="lg" onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 px-12" data-testid="final-submit-btn">
            {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Grading your answers...</> : <>Submit Exam ({answered}/{exam.questions.length} answered)</>}
          </Button>
        </div>
      </div>
    );
  }

  // ==================== RESULTS VIEW ====================
  if (view === 'results' && results) {
    const mcqResults = (results.results || []).filter(r => r.type === 'mcq');
    const writtenResults = (results.results || []).filter(r => r.type !== 'mcq');

    return (
      <div className="space-y-5" data-testid="results-view">
        <Button variant="outline" onClick={() => { setView('subjects'); setResults(null); fetchSubjects(); }}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Subjects
        </Button>

        {/* Score Card */}
        <Card className={`border-2 ${results.percentage >= 60 ? 'border-emerald-500/50' : results.percentage >= 40 ? 'border-amber-500/50' : 'border-red-500/50'}`}>
          <CardContent className="p-6 text-center">
            <div className={`w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-bold ${results.percentage >= 60 ? 'bg-emerald-500/20 text-emerald-400' : results.percentage >= 40 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>
              {results.percentage}%
            </div>
            <h3 className="text-xl font-bold">{results.score}/{results.total_marks} marks</h3>
            <p className="text-muted-foreground text-sm">{results.subject || results.subject_title}</p>
            <div className="flex justify-center gap-4 mt-3 text-sm">
              <span className="text-emerald-400">MCQ: {mcqResults.filter(r => r.is_correct).length}/{mcqResults.length}</span>
              <span>Written: {writtenResults.reduce((s,r) => s + r.score, 0)}/{writtenResults.reduce((s,r) => s + r.max_marks, 0)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Weak & Strong Topics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {results.weak_topics?.length > 0 && (
            <Card className="border-red-500/20">
              <CardContent className="p-4">
                <h4 className="font-bold text-sm flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Weak Topics</h4>
                <div className="flex flex-wrap gap-1">{results.weak_topics.map(t => <Badge key={t} variant="outline" className="text-red-400 border-red-500/30 text-xs">{t}</Badge>)}</div>
              </CardContent>
            </Card>
          )}
          {results.strong_topics?.length > 0 && (
            <Card className="border-emerald-500/20">
              <CardContent className="p-4">
                <h4 className="font-bold text-sm flex items-center gap-2 mb-2"><Award className="w-4 h-4 text-emerald-400" /> Strong Topics</h4>
                <div className="flex flex-wrap gap-1">{results.strong_topics.map(t => <Badge key={t} variant="outline" className="text-emerald-400 border-emerald-500/30 text-xs">{t}</Badge>)}</div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* MCQ Results */}
        <Card>
          <CardHeader><CardTitle className="text-base">Section A — MCQ Results ({mcqResults.filter(r=>r.is_correct).length}/{mcqResults.length})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {mcqResults.map((r, i) => (
              <div key={i} className={`p-3 rounded-lg border ${r.is_correct ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                <div className="flex items-start gap-2">
                  {r.is_correct ? <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />}
                  <div className="flex-1">
                    <p className="text-sm font-medium">Q{i+1}. {r.question}</p>
                    <div className="mt-1 text-xs space-y-0.5">
                      {!r.is_correct && <p className="text-red-400">Your answer: {r.student_answer || '(not answered)'}</p>}
                      <p className="text-emerald-400 font-medium">Correct: {r.correct_answer}</p>
                      <p className="text-muted-foreground mt-1">{r.explanation}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Written Results */}
        <Card>
          <CardHeader><CardTitle className="text-base">Written Answer Results</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {writtenResults.map((r, i) => (
              <div key={i} className="p-4 rounded-lg border">
                <div className="flex justify-between items-start mb-2">
                  <p className="text-sm font-medium flex-1">Q{mcqResults.length+i+1}. {r.question}</p>
                  <Badge className={r.score >= r.max_marks * 0.6 ? 'bg-emerald-600' : r.score >= r.max_marks * 0.3 ? 'bg-amber-600' : 'bg-red-600'}>
                    {r.score}/{r.max_marks}
                  </Badge>
                </div>
                {r.student_answer && (
                  <div className="p-2 rounded bg-muted/50 text-xs mb-2">
                    <p className="font-medium text-muted-foreground mb-1">Your Answer:</p>
                    <p>{r.student_answer}</p>
                  </div>
                )}
                {r.feedback && (
                  <div className="p-2 rounded bg-blue-500/10 text-xs mb-2">
                    <p className="font-medium text-blue-400 mb-1">AI Feedback:</p>
                    <p>{r.feedback}</p>
                  </div>
                )}
                <div className="p-2 rounded bg-emerald-500/10 text-xs">
                  <p className="font-medium text-emerald-400 mb-1">Model Answer:</p>
                  <p>{r.model_answer}</p>
                  {r.key_points?.length > 0 && (
                    <div className="mt-1"><span className="font-medium">Key Points: </span>{r.key_points.join(' | ')}</div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Retake */}
        <div className="text-center pb-8">
          <Button onClick={() => { setView('subjects'); setResults(null); fetchSubjects(); }} variant="outline" className="mr-3">
            <ArrowLeft className="w-4 h-4 mr-2" /> All Tests
          </Button>
          <Button onClick={() => startExam(results.subject_id || (results.results?.[0]?.subject_id))} className="bg-primary">
            <RefreshCw className="w-4 h-4 mr-2" /> Retake This Test
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
